import apolloClient from '@/plugins/apollo';
import { ApolloQueryResult } from '@apollo/client/core';
import Stores from '@/stores';
import DateTime from '@/utils/DateTime';
import Service from '@/definitions/interfaces/Service.i';
import FullService from '@/definitions/interfaces/FullService.i';
import Institution from '@/definitions/interfaces/Institution.i';
import EstablishmentInput from '@/definitions/interfaces/EstablishmentInput.i';
import Establishment from '@/definitions/interfaces/Establishment.i';
import InstitutionInput from '@/definitions/interfaces/InstitutionInput.i';
import AddressInput from '@/definitions/interfaces/AddressInput.i';
import BankAccountInput from '@/definitions/interfaces/BankAccountInput.i';
import SubstituteSpecializationInput from '@/definitions/interfaces/SubstituteSpecializationInput.i';
import EstablishmentDoctor from '@/definitions/interfaces/EstablishmentDoctor.i';
import EstablishmentDoctorInput from '@/definitions/interfaces/EstablishmentDoctorInput.i';
import ServiceInformation from '@/definitions/interfaces/ServiceInformation.i';
import ServiceArea from '@/definitions/interfaces/ServiceArea.i';
import ServiceInput from '@/definitions/interfaces/ServiceInput.i';
import ServiceAreaMobile from '@/definitions/interfaces/ServiceAreaMobile.i';
import ServiceAreaOffice from '@/definitions/interfaces/ServiceAreaOffice.i';
import Substitute from '@/definitions/interfaces/Substitute.i';
import LiabilityInsurance from '@/definitions/interfaces/LiabilityInsurance.i';
import User from '@/definitions/interfaces/User.i';
import Debtor from '@/definitions/interfaces/Debtor.i';
import Address from '@/definitions/interfaces/Address.i';
import BankAccount from '@/definitions/interfaces/BankAccount.i';
import Event from '@/definitions/interfaces/Event.i';
import ListEventsResult from '@/definitions/interfaces/ListEventsResult.i';
import Sort from '@/utils/Sort';
import Upload from '@/utils/Upload';
import Validation from '@/services/Validation';

import {
  CREATE_SERVICE_AREA,
  UPDATE_SERVICE_AREA,
  CREATE_SERVICE_AREA_MOBILE,
  CREATE_SERVICE_AREA_OFFICE,
  UPDATE_SERVICE_AREA_MOBILE,
  UPDATE_SERVICE_AREA_OFFICE,
  UPDATE_ADDRESSES,
  UPDATE_CUSTOMER_PROFILE_OPERATOR,
  UPDATE_SUBSTITUTE_OPERATOR,
  UPDATE_BANKACCOUNTS_OPERATOR,
  UPDATE_SPECIALIZATION_OPERATOR,
  UPDATE_INSTITUTIONS_OPERATOR,
  UPDATE_ESTABLISHMENTS_OPERATOR,
  UPDATE_ESTABLISHMENT_DOCTOR_OPERATOR,
  UPDATE_SERVICE,
  PUBLISH_SERVICE,
  DELETE_SERVICE,
  CONCLUDE_SERVICE,
  ASSIGN_SERVICE,
  DELETE_SERVICE_ASSIGNEMENT,
  UPDATE_DOCUMENT_LIABILITY_INSURANCE,
  DELETE_ESTABLISHMENT_DOCTOR_OPERATOR,
  DELETE_DOCUMENT,
  ADD_ESTABLISHMENT_DOCTOR_OPERATOR,
  SUBMIT_MOBILESERVICE_OPERATOR,
  SUBMIT_OFFICESERVICE_OPERATOR,
  ADD_APPLICANT_TO_SERVICE_OPERATOR,
  REVOKE_APPLICATION_OPERATOR,
  UPDATE_SUBSTITUTE_BLACKLIST,
  SEND_SERVICE_NEWSLETTER_OPERATOR,
  SEND_SERVICE_NEWSLETTER_BY_STATE_OPERATOR,
  DELETE_ESTABLISHMENT_OPERATOR,
  DEACTIVATE_USER,
  CREATE_SUBSTITUTE_OPERATOR,
  CREATE_PARTNER_OPERATOR,
  UPDATE_PARTNER_LOGISTICS_PREFERENCES_OPERATOR,
  SEND_ORDER_CONFIMRATION_OPERATOR,
  UPDATE_SERVICE_LOGISTICS,
  KAFKA_PRODUCE_ALL_SERVICES,
  SEND_ORDER_SUMMARY_OPERATOR,
  DELETE_DEBTOR_BILL_PAYMENT_METHOD,
  DELETE_DEBTOR_SEPA_PAYMENT_METHOD,
  UPDATE_LEGACY_INFORMATION,
  SEND_DOCUMENTS_OPERATOR,
  SEND_USERS_MASS_EMAIL_OPERATOR,
  CANCEL_SERVICE,
  DELETE_INSURANCE_DOCUMENT,
  DELETE_LICENSE_DOCUMENT,
  DELETE_SPECIALIZATION_DOCUMENT,
  REACTIVATE_USER,
  DELETE_MEASLES_PROOF_DOCUMENT,
  UPDATE_DOCUMENT_MEASLES_IMMUNITY_PROOF,
  DUPLICATE_SERVICE,
} from '@/data/gql/operator/mutations';

import {
  LIST_USERS_OPERATOR,
  LIST_USERS_FULL_OPERATOR,
  LIST_USERS_FUZZY_OPERATOR,
  OPERATOR_LOAD_SERVICE_AREA,
  FETCH_SUBSTITUTE_SPECIALIZATIONS_OPERATOR,
  FETCH_SUBSTITUTE_LICENSE_OPERATOR,
  FETCH_SUBSTITUTE_INSURANCES_OPERATOR,
  LIST_USERS_PAGINATED_OPERATOR,
  OPERATOR_FETCH_ACCOUNTING_MOBILE,
  OPERATOR_FETCH_ACCOUNTING_OFFICE,
  GET_SUBSITUTE_BLACKLIST,
  LIST_EVENTS,
  FETCH_CUSTOMER_LOGISTICS_OPERATOR,
  GET_FILE_FROM_S3,
  PROVIDE_COGNITO_ACCESS,
  LIST_SERVICES_PAGINATED_OPERATOR,
  FETCH_SUBSTITUTE_MEASLES_PROOF_OPERATOR,
} from '@/data/gql/operator/new_queries';

import { SERVICE_AREAS_OPERATOR, SERVICE_DETAIL_OPERATOR, LIST_SERVICES_WITH_LOGISTICS_OPERATOR, LIST_SERVICES_OPERATOR } from '@/data/gql/operator/queries';

import { CREATE_ESTABLISHMENTS } from '@/data/gql/mutations';

import { SPECIALIZATIONS_QUERY, SERVICE_INFORMATION } from '@/data/gql/queries';
import { SUBMIT_SUBSTITUTE_CHECKLIST, ADD_PAYMENT_METHOD_BILL, ADD_PAYMENT_METHOD_SEPA, SET_PARTNER_TARIFF } from '@/data/gql/mutations';
import { MOBILE_SERVICES_QUERY, MOBILE_SERVICES_WITH_DEBTOR_QUERY, OFFICE_SERVICES_QUERY, OFFICE_SERVICES_WITH_DEBTOR_QUERY } from '@/data/gql/misc_queries';
import PartnerLogisticsPreferences from '@/definitions/interfaces/PartnerLogisticsPreferences.i';
import { Item } from 'vue3-easy-data-table';
import { ServiceLogistics } from '@/definitions/interfaces/ServiceLogistics';
import { Filters, ServiceWithLogistics } from '@/views/operators/logisticsHub/types';
import MeaslesProof from '@/definitions/interfaces/MeaslesProof.i';

class Operator {
  /**
   * fetch users
   *
   */
  public async fetchUsers(searchTerm: string, roles: string[], _verified: boolean | null): Promise<User[]> {
    const _fuzzy: boolean = isNaN(parseInt(searchTerm));

    if (_fuzzy) {
      // build the where block
      const _where = {
        AND: [
          {
            OR: [
              { first_name: { contains: searchTerm, mode: 'insensitive' } },
              { last_name: { contains: searchTerm, mode: 'insensitive' } },
              { email: { contains: searchTerm, mode: 'insensitive' } },
              {
                institution_ownership: {
                  institution: {
                    name: { contains: searchTerm, mode: 'insensitive' },
                  },
                },
              },
            ],
          },
          {
            user_role: {
              some: {
                role: {
                  in: roles,
                },
              },
            },
          },
        ],
      };
      const _result = await apolloClient.defaultClient.query({
        query: LIST_USERS_FUZZY_OPERATOR,
        variables: {
          where: _where,
        },
        fetchPolicy: 'no-cache',
      });

      return _result.data.users;
    } else {
      const _result = await apolloClient.defaultClient.query({
        query: LIST_USERS_OPERATOR,
        variables: {
          debtorNumber: parseInt(searchTerm),
          roles: roles,
        },
        fetchPolicy: 'no-cache',
      });

      return _result.data.listCustomers;
    }
  }

  /**
   * search users with pagination
   */
  public async searchUsersPaginated({
    searchTerm,
    roles,
    skip,
    first,
    sortBy,
    sortType,
  }: {
    searchTerm: string;
    roles: string[];
    skip: number;
    first: number;
    sortBy: string;
    sortType: 'asc' | 'desc';
  }): Promise<{ users: User[]; totalCount: number }> {
    const isFuzzy: boolean = isNaN(parseInt(searchTerm));

    const where = isFuzzy
      ? {
          AND: [
            {
              OR: [
                { first_name: { contains: searchTerm, mode: 'insensitive' } },
                { last_name: { contains: searchTerm, mode: 'insensitive' } },
                { email: { contains: searchTerm, mode: 'insensitive' } },
                { institution_ownership: { institution: { name: { contains: searchTerm, mode: 'insensitive' } } } },
              ],
            },
            {
              user_role: {
                some: {
                  role: {
                    in: roles,
                  },
                },
              },
            },
          ],
        }
      : {
          OR: [
            { substitute_substitute_user_idTouser: { debtor: { debtorNumber: { equals: parseInt(searchTerm) } } } },
            { institution_ownership: { institution: { debtor: { debtorNumber: { equals: parseInt(searchTerm) } } } } },
          ],
          user_role: { some: { role: { in: roles } } },
        };

    const fieldMap: Record<string, string> = {
      firstname: 'first_name',
      lastname: 'last_name',
      email: 'email',
      registrationDate: 'registration_date',
      debtorNumber: 'debtor_number',
      institutionName: 'institution_name',
    };
    const mappedField = fieldMap[sortBy] || 'debtor_number';
    const mappedDirection = sortType.toLocaleLowerCase();

    const orderBy = [
      {
        field: mappedField,
        direction: mappedDirection,
      },
    ];

    const result = await apolloClient.defaultClient.query({
      query: LIST_USERS_PAGINATED_OPERATOR,
      variables: {
        where,
        orderBy,
        skip,
        first,
      },
      fetchPolicy: 'no-cache',
    });

    const { totalCount, users } = result.data.paginatedUsers;
    return { users, totalCount };
  }

  /**
   * fetch a single user with all available data
   *
   */
  public async fetchSingleUser(user_id: string): Promise<User | null> {
    const _result = await apolloClient.defaultClient.query({
      query: LIST_USERS_FULL_OPERATOR,
      variables: {
        userID: user_id,
      },
      fetchPolicy: 'no-cache',
    });
    if (_result.data.listCustomers.length === 0) {
      return null;
    }
    let _user = _result.data.listCustomers[0];
    if (_user.institutions.length > 0) {
      if (_user.institutions[0].debtor?.tariff === null) {
        const _tariffCreated = await this.repairTariff(_user.institutions[0].debtor);
        if (_tariffCreated) {
          const _refetch = await apolloClient.defaultClient.query({
            query: LIST_USERS_FULL_OPERATOR,
            variables: {
              userID: user_id,
            },
            fetchPolicy: 'no-cache',
          });
          if (_refetch.data.listCustomers.length === 0) {
            return null;
          }
          _user = _refetch.data.listCustomers[0];
        }
      }
    }
    if (_user.substitute !== null) {
      if (_user.substitute.debtor?.payment_method.length === 0) {
        const [_addpayment, _paymentMethodId] = await this.addBillPaymentMethod(_user.substitute.debtor?.id);
        if (_addpayment) {
          const _refetch = await apolloClient.defaultClient.query({
            query: LIST_USERS_FULL_OPERATOR,
            variables: {
              userID: user_id,
            },
            fetchPolicy: 'no-cache',
          });
          if (_refetch.data.listCustomers.length === 0) {
            return null;
          }
          _user = _refetch.data.listCustomers[0];
        }
      }
    }
    return _user;
  }

  /**
   * update a user's basedata
   * @param {User} user
   */
  public async updateProfile(user: User): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_CUSTOMER_PROFILE_OPERATOR,
      variables: {
        userID: user.id,
        memo: user.memo,
        title: user.title,
        gender: user.gender,
        firstName: user.firstName,
        lastName: user.lastName,
        birthDate: DateTime.dateToIsoDateString(user.birthDate as Date),
        mobile1: user.mobile1,
        mobile2: user.mobile2,
        fax: user.fax,
        phone: user.phone,
        contactEmail: user.contactEmail,
        serviceFeeValue: user.serviceFeeValue ? Number(user.serviceFeeValue) : null,
        serviceFeeFrequency: user.serviceFeeFrequency || null,
        serviceFeeFirstChargeDate: user.serviceFeeFirstChargeDate ? DateTime.dateToIsoDateString(user.serviceFeeFirstChargeDate) : null,
        suspendedContractFee: user.suspendedContractFee === null ? null : parseInt(user.suspendedContractFee),
        activeContract: user.activeContract,
        invoicingEmail: user.invoicingEmail,
        specialityArea: user.speciality_area,
        bagHandlingFee: user.bag_handling_fee ? Number(user.bag_handling_fee) : null,
      },
    });
    return _result.data.updateCustomerProfile;
  }

  /**
   * update a partner
   * @param {PartnerLogisticsPreferences} partnerLogisticsPreferences
   */
  public async updatePartnerLogisticsPreferences(partnerLogisticsPreferences: PartnerLogisticsPreferences): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_PARTNER_LOGISTICS_PREFERENCES_OPERATOR,
      variables: {
        id: partnerLogisticsPreferences.id,
        requiresDoctorsBagDelivery: partnerLogisticsPreferences.requiresDoctorsBagDelivery,
        requiresFolderDelivery: partnerLogisticsPreferences.requiresFolderDelivery,
        requiresCardReader: partnerLogisticsPreferences.requiresCardReader,
        cardReaderSerialNumbers: partnerLogisticsPreferences.cardReaderSerialNumbers,
      },
    });
    return _result.data.updatePartnerLogisticsPreferences;
  }

  /**
   * fetch the specializations that are available in the system
   */
  public async fetchSpecializations(): Promise<void> {
    const _result = await apolloClient.defaultClient.query({
      query: SPECIALIZATIONS_QUERY,
      fetchPolicy: 'no-cache',
    });

    // set cache in store
    Stores.operatorData.specializationsCache = _result.data.listSpecializations;
  }

  /**
   * fetch assignments
   * @return {Promise}
   */
  async fetchAssignmentsForSubstitute(substitute_id: string, from: Date | null, to: Date | null): Promise<FullService[]> {
    let _where: Object = {};

    if (from === null && to === null) {
      _where = { service: { assignment: { substitute_id: { equals: substitute_id } } } };
    }

    if (from) {
      _where = {
        service: {
          AND: [
            {
              assignment: {
                substitute_id: {
                  equals: substitute_id,
                },
              },
            },
            {
              start: {
                gte: from,
              },
            },
          ],
        },
      };
    }

    if (to) {
      _where = {
        service: {
          AND: [
            {
              assignment: {
                substitute_id: {
                  equals: substitute_id,
                },
              },
            },
            {
              start: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    if (from && to) {
      _where = {
        service: {
          AND: [
            {
              assignment: {
                substitute_id: {
                  equals: substitute_id,
                },
              },
            },
            {
              start: {
                gte: from,
              },
            },
            {
              start: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    let _assignments = [];

    const _mobileAssignments = await apolloClient.defaultClient.query({
      query: MOBILE_SERVICES_WITH_DEBTOR_QUERY,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });
    const _officeAssignments = await apolloClient.defaultClient.query({
      query: OFFICE_SERVICES_WITH_DEBTOR_QUERY,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    for (const newAssignment of _officeAssignments.data.officeServices) {
      const _assignmentReorder: Service = {
        ...newAssignment.details,
        secondaryServiceArea: newAssignment.serviceArea.name,
        secondaryServiceAreaAddress: newAssignment.serviceArea.address
          ? `${newAssignment.serviceArea.address.street} ${newAssignment.serviceArea.address.houseNr} ${newAssignment.serviceArea.address.city} ${newAssignment.serviceArea.address.zipCode} ${newAssignment.serviceArea.address.country}`
          : '',
      };
      _assignmentReorder.type = newAssignment.__typename;
      _assignments.push(_assignmentReorder);
    }
    for (const newAssignment of _mobileAssignments.data.mobileServices) {
      const _assignmentReorder: Service = {
        ...newAssignment.details,
        secondaryServiceArea: newAssignment.serviceArea.name,
        secondaryServiceAreaAddress: newAssignment.serviceArea.address
          ? `${newAssignment.serviceArea.address.street} ${newAssignment.serviceArea.address.houseNr} ${newAssignment.serviceArea.address.city} ${newAssignment.serviceArea.address.zipCode} ${newAssignment.serviceArea.address.country}`
          : '',
      };
      _assignmentReorder.type = newAssignment.__typename;
      _assignments.push(_assignmentReorder);
    }

    _assignments = Sort.arraySortBy(_assignments, (service) => [service.start], 1);
    return _assignments;
  }

  /**
   * fetch applications
   * @return {Promise}
   */
  async fetchApplicationsForSubstitute(substitute_id: string, from: Date | null, to: Date | null): Promise<FullService[]> {
    let _where: Object = {};

    if (from === null && to === null) {
      _where = { service: { application: { some: { substitute_id: { equals: substitute_id } } } } };
    }

    if (from) {
      _where = {
        AND: [
          {
            service: {
              application: {
                some: {
                  substitute_id: {
                    equals: substitute_id,
                  },
                },
              },
            },
          },
          {
            service: {
              start: {
                gte: from,
              },
            },
          },
        ],
      };
    }

    if (to) {
      _where = {
        AND: [
          {
            service: {
              application: {
                some: {
                  substitute_id: {
                    equals: substitute_id,
                  },
                },
              },
            },
          },
          {
            service: {
              start: {
                lte: to,
              },
            },
          },
        ],
      };
    }

    if (from && to) {
      _where = {
        AND: [
          {
            service: {
              application: {
                some: {
                  substitute_id: {
                    equals: substitute_id,
                  },
                },
              },
            },
          },
          {
            service: {
              start: {
                gte: from,
              },
            },
          },
          {
            service: {
              start: {
                lte: to,
              },
            },
          },
        ],
      };
    }

    let _applications = [];

    const _mobileApplications = await apolloClient.defaultClient.query({
      query: MOBILE_SERVICES_QUERY,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });
    const _officeApplications = await apolloClient.defaultClient.query({
      query: OFFICE_SERVICES_QUERY,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    for (const newApplication of _officeApplications.data.officeServices) {
      const _applicationReorder: Service = newApplication.details;
      _applicationReorder.type = newApplication.__typename;
      _applications.push(_applicationReorder);
    }
    for (const newApplication of _mobileApplications.data.mobileServices) {
      const _applicationReorder: Service = newApplication.details;
      _applicationReorder.type = newApplication.__typename;
      _applications.push(_applicationReorder);
    }

    _applications = Sort.arraySortBy(_applications, (service) => [service.start], 1);
    return _applications;
  }

  /**
   * update a substitute
   * @param {Substitute} substitute
   */
  public async updateSubstitute(substitute: Substitute): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute.id,
        memo: substitute.memo,
        trainingConfirmed: substitute.trainingConfirmed,
        taxNumber: substitute.taxNumber,
        verified: substitute.verified,
        has_doctors_bag: substitute.has_doctors_bag,
        specialization: substitute.specializations[0].specialization,
      },
    });
    return _result.data.updateSubstitute;
  }

  /**
   * update a substitute
   * @param {Substitute} substitute
   */
  public async confirmTraining(substitute_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute_id,
        trainingConfirmed: true,
      },
    });
    return _result.data.updateSubstitute;
  }

  /**
   * verify an existing substitute
   *
   */
  public async verifySubstitute(substitute_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute_id,
        verified: true,
      },
    });
    return _result.data.updateSubstitute;
  }

  /**
   * unverify an existing substitute
   *
   */
  public async unverifySubstitute(substitute_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute_id,
        verified: false,
      },
    });
    return _result.data.updateSubstitute;
  }

  /**
   * update a substitute's specialization certificate
   * @param {File} certificate file
   * @param {string} specialization_id
   * @return {Promise.<boolean>}
   */
  public async updateSubstituteSpecializationCertificate(specialization_id: string, certificate: File): Promise<boolean> {
    if (certificate !== null) {
      const _response = await apolloClient.defaultClient.mutate({
        mutation: UPDATE_SPECIALIZATION_OPERATOR,
        variables: {
          id: specialization_id,
          certificate: await Upload.getUploadData(certificate),
        },
        context: {
          hasUpload: true,
        },
      });
      return _response.data.updateSubstituteSpecializations;
    } else {
      return false;
    }
  }

  /**
   * verify a substitute's specialization
   * @param {string} specialization_id
   * @return {Promise.<boolean>}
   */
  public async verifySubstituteSpecialization(specialization_id: string): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SPECIALIZATION_OPERATOR,
      variables: {
        id: specialization_id,
        verified: true,
      },
    });
    return _response.data.updateSubstituteSpecializations;
  }

  /**
   * update a substitute's license
   * @param {File} license file
   * @param {string} substitute_id
   * @return {Promise.<boolean>}
   */
  public async updateSubstituteLicense(substitute_id: string, license: File): Promise<boolean> {
    if (license !== null) {
      const _result = await apolloClient.defaultClient.mutate({
        mutation: UPDATE_SUBSTITUTE_OPERATOR,
        variables: {
          substituteID: substitute_id,
          license: await Upload.getUploadData(license),
        },
      });
      return _result.data.updateSubstitute;
    } else {
      return false;
    }
  }

  /**
   * Update an existing insurance
   *
   */
  public async updateSubstituteInsurance(insurance: LiabilityInsurance): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_DOCUMENT_LIABILITY_INSURANCE,
      variables: {
        id: insurance.id,
        validFrom: DateTime.dateToIsoDateString(insurance.validFrom),
        validTo: DateTime.dateToIsoDateString(insurance.validTo),
        insuranceName: insurance.insuranceName,
        insuranceNumber: insurance.insuranceNumber,
      },
    });
    return _result.data.updateDocumentLiabilityInsurance;
  }

  public async updateSubstituteMeaslesProof(proof: MeaslesProof): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_DOCUMENT_MEASLES_IMMUNITY_PROOF,
      variables: {
        id: proof.id,
        validFrom: DateTime.dateToIsoDateString(proof.validFrom),
        validTo: DateTime.dateToIsoDateString(proof.validTo),
        specification: proof.specification,
      },
    });
    return _result.data.updateDocumentLiabilityInsurance;
  }

  /**
   * add an insurance to a substitute()
   * @param {string} insuranceName
   * @param {string} insuranceNumber
   * @param {Date} validFrom
   * @param {Date} validTo
   * @param {File} insurance_police
   * @param {boolean} cache - also update the cache
   * @return {Promise.<boolean>}
   */
  public async addSubstituteInsurance(
    substitute_id: string,
    insuranceName: string,
    insuranceNumber: string,
    validFrom: Date,
    validTo: Date,
    insurancePolice: File,
  ): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute_id,
        liabilityInsurance: {
          insuranceName: insuranceName,
          insuranceNumber: insuranceNumber,
          validFrom: DateTime.dateToIsoDateString(validFrom),
          validTo: DateTime.dateToIsoDateString(validTo),
          document: await Upload.getUploadData(insurancePolice),
        },
        context: {
          hasUpload: true,
        },
      },
    });
    return _result.data.updateSubstitute;
  }

  public async addSubstituteMeaslesProof(
    substitute_id: string,
    validFrom: Date,
    validTo: Date,
    specification: string,
    insurancePolice: File,
  ): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID: substitute_id,
        immunityProofVerified: false,
        measlesImmunityProof: {
          validFrom: DateTime.dateToIsoDateString(validFrom),
          validTo: DateTime.dateToIsoDateString(validTo),
          specification: specification,
          document: await Upload.getUploadData(insurancePolice),
        },
        context: {
          hasUpload: true,
        },
      },
    });
    return _result.data.updateSubstitute;
  }

  /**
   * Delete an existing insurance document
   *
   */
  public async deleteInsuranceDocument(document_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_INSURANCE_DOCUMENT,
      variables: {
        documentID: document_id,
      },
    });
    return _result.data.deleteInsuranceDocument;
  }

  public async deleteMeaslesProofDocument(document_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_MEASLES_PROOF_DOCUMENT,
      variables: {
        documentID: document_id,
      },
    });
    return _result.data.deleteMeaslesProofDocument;
  }

  /**
   * Delete an existing license document
   *
   */
  public async deleteLicenseDocument(substituteId: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_LICENSE_DOCUMENT,
      variables: {
        substituteID: substituteId,
      },
    });
    return _result.data.deleteInsuranceDocument;
  }

  /**
   * Delete an existing specialization document
   *
   */
  public async deleteSpecializationDocument(specializationId: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_SPECIALIZATION_DOCUMENT,
      variables: {
        specializationID: specializationId,
      },
    });
    return _result.data.deleteInsuranceDocument;
  }

  /**
   * Delete an existing document
   *
   */
  public async deleteDocument(document_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_DOCUMENT,
      variables: {
        documentID: document_id,
      },
    });
    return _result.data.deleteDocument;
  }

  /**
   * fetch services for a given array of establishments
   *
   */
  public async fetchServicesForPartner(establishments: Establishment[], from: Date | null, to: Date | null): Promise<FullService[]> {
    let _services = [];
    for (const establishment of establishments) {
      let _where: Object = {};

      if (from === null && to === null) {
        _where = { service: { establishment_id: { equals: establishment.id } } };
      }

      if (from) {
        _where = {
          service: {
            AND: [
              {
                establishment_id: {
                  equals: establishment.id,
                },
              },
              {
                start: {
                  gte: from,
                },
              },
            ],
          },
        };
      }

      if (to) {
        _where = {
          service: {
            AND: [
              {
                establishment_id: {
                  equals: establishment.id,
                },
              },
              {
                start: {
                  lte: to,
                },
              },
            ],
          },
        };
      }

      if (from && to) {
        _where = {
          service: {
            AND: [
              {
                establishment_id: {
                  equals: establishment.id,
                },
              },
              {
                start: {
                  gte: from,
                },
              },
              {
                start: {
                  lte: to,
                },
              },
            ],
          },
        };
      }

      const _mobileServices = await apolloClient.defaultClient.query({
        query: MOBILE_SERVICES_QUERY,
        variables: {
          where: _where,
        },
        fetchPolicy: 'no-cache',
      });

      for (const newService of _mobileServices.data.mobileServices) {
        const _serviceReorder: Service = {
          ...newService.details,
          secondaryServiceArea: newService.serviceArea.name,
          secondaryServiceAreaAddress: newService.serviceArea.address
            ? `${newService.serviceArea.address.street} ${newService.serviceArea.address.houseNr} ${newService.serviceArea.address.city} ${newService.serviceArea.address.zipCode} ${newService.serviceArea.address.country}`
            : '',
        };
        _serviceReorder.type = newService.__typename;
        _services.push(_serviceReorder);
      }

      const _officeServices = await apolloClient.defaultClient.query({
        query: OFFICE_SERVICES_QUERY,
        variables: {
          where: _where,
        },
        fetchPolicy: 'no-cache',
      });
      for (const newService of _officeServices.data.officeServices) {
        const _serviceReorder: Service = {
          ...newService.details,
          secondaryServiceArea: newService.serviceArea.name,
          secondaryServiceAreaAddress: newService.serviceArea.address
            ? `${newService.serviceArea.address.street} ${newService.serviceArea.address.houseNr} ${newService.serviceArea.address.city} ${newService.serviceArea.address.zipCode} ${newService.serviceArea.address.country}`
            : '',
        };
        _serviceReorder.type = newService.__typename;
        _services.push(_serviceReorder);
      }
    }
    _services = Sort.arraySortBy(_services, (service) => [service.start], 1);
    return _services;
  }

  /**
   * update an existing institution
   *
   */
  public async updateInstitution(institution: Institution): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_INSTITUTIONS_OPERATOR,
      variables: {
        institutions: [
          {
            id: institution.id,
            bsnr: institution.bsnr,
            name: institution.name,
            type: institution.type,
            verified: institution.verified,
          },
        ],
      },
    });
    if (_result.data.updateInstitutions[0] === 'succeeded') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * update an existing institution
   *
   */
  public async verifyInstitution(institution_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_INSTITUTIONS_OPERATOR,
      variables: {
        institutions: [
          {
            id: institution_id,
            verified: true,
          },
        ],
      },
    });
    if (_result.data.updateInstitutions[0] === 'succeeded') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * update an existing establishment
   *
   */
  public async updateEstablishment(establishment: Establishment): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_ESTABLISHMENTS_OPERATOR,
      variables: {
        establishments: [
          {
            id: establishment.id,
            name: establishment.name,
            bsnr: establishment.bsnr,
            serviceAreaID: establishment.serviceArea !== null ? establishment.serviceArea.id : null,
          },
        ],
      },
    });
    if (_result.data.updateEstablishments[0] === 'succeeded') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * add an establishment
   *
   */
  public async addEstablishment(newEstablishment: EstablishmentInput): Promise<string> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: CREATE_ESTABLISHMENTS,
      variables: {
        establishments: [newEstablishment],
      },
    });

    if (Validation.validateUUIDPattern(_response.data.createEstablishments[0])) {
      return _response.data.createEstablishments[0];
    }
    return '';
  }

  /**
   * delete an establishment
   *
   */
  public async deleteEstablishment(establishment_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_ESTABLISHMENT_OPERATOR,
      variables: {
        establishmentID: establishment_id,
      },
    });
    return _result.data.deleteEstablishmentOperator;
  }

  /**
   * set the service area for an establishment
   *
   */
  public async setEstablishmentServiceArea(establishment_id: string, serviceArea_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_ESTABLISHMENTS_OPERATOR,
      variables: {
        establishments: [
          {
            id: establishment_id,
            serviceAreaID: serviceArea_id,
          },
        ],
      },
    });
    if (_result.data.updateEstablishments[0] === 'succeeded') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * add a new establishment doctor
   *
   */
  public async addEstablishmentDoctor(establishment_id: string, newDoctor: EstablishmentDoctor): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: ADD_ESTABLISHMENT_DOCTOR_OPERATOR,
      variables: {
        establishmentID: establishment_id,
        name: newDoctor.name,
        kvSeats: parseFloat(newDoctor.kvSeats as unknown as string),
        lanr: newDoctor.lanr,
      },
    });
    return _result.data.addEstablishmentDoctor;
  }

  /**
   * update an existing establishment doctor
   *
   */
  public async updateEstablishmentDoctor(doctor: EstablishmentDoctor): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_ESTABLISHMENT_DOCTOR_OPERATOR,
      variables: {
        establishmentDoctorID: doctor.id,
        name: doctor.name,
        kvSeats: parseFloat(doctor.kvSeats as unknown as string),
        lanr: doctor.lanr,
      },
    });
    return _result.data.updateEstablishmentDoctor;
  }

  /**
   * delete an existing establishment doctor
   *
   */
  public async deleteEstablishmentDoctor(doctor_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_ESTABLISHMENT_DOCTOR_OPERATOR,
      variables: {
        establishmentDoctorID: doctor_id,
      },
    });
    return _result.data.deleteEstablishmentDoctorOperator;
  }

  /**
   * fetch a list of services to display in a listing
   *
   */
  public async fetchAllInclusiveServices(from: Date | null, to: Date | null): Promise<FullService[]> {
    let _services = [];

    let _where: Object = {};

    if (from === null && to === null) {
      _where = {
        service: {
          allinclusive: {
            equals: true,
          },
        },
      };
    }

    if (from) {
      _where = {
        service: {
          AND: [
            {
              allinclusive: {
                equals: true,
              },
            },
            {
              created_on: {
                gte: from,
              },
            },
          ],
        },
      };
    }

    if (to) {
      _where = {
        service: {
          AND: [
            {
              allinclusive: {
                equals: true,
              },
            },
            {
              created_on: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    if (from && to) {
      _where = {
        service: {
          AND: [
            {
              allinclusive: {
                equals: true,
              },
            },
            {
              created_on: {
                gte: from,
              },
            },
            {
              created_on: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    const _mobileServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_MOBILE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _mobileServices.data.mobileServices) {
      _services.push(_service);
    }

    const _officeServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_OFFICE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _officeServices.data.officeServices) {
      _services.push(_service);
    }

    _services = Sort.arraySortBy(_services, (service: FullService) => [service.details.createdOn], 1);
    return _services;
  }

  /**
   * fetch a list of services to display in a listing
   *
   */
  public async fetchServicesAccordingToTier(from: Date | null, to: Date | null, tier: string): Promise<FullService[]> {
    let _services = [];

    let _where: Object = {};

    if (from === null && to === null) {
      _where = {
        service: {
          service_tier: {
            contains: tier,
          },
        },
      };
    }

    if (from) {
      _where = {
        service: {
          AND: [
            {
              service_tier: {
                contains: tier,
              },
            },
            {
              created_on: {
                gte: from,
              },
            },
          ],
        },
      };
    }

    if (to) {
      _where = {
        service: {
          AND: [
            {
              service_tier: {
                contains: tier,
              },
            },
            {
              created_on: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    if (from && to) {
      _where = {
        service: {
          AND: [
            {
              service_tier: {
                contains: tier,
              },
            },
            {
              created_on: {
                gte: from,
              },
            },
            {
              created_on: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    const _mobileServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_MOBILE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _mobileServices.data.mobileServices) {
      _services.push(_service);
    }

    const _officeServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_OFFICE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _officeServices.data.officeServices) {
      _services.push(_service);
    }

    _services = Sort.arraySortBy(_services, (service: FullService) => [service.details.createdOn], 1);
    return _services;
  }

  /**
   * fetch a list of services to display in a listing
   *
   */
  public async fetchServices(
    from: Date | null,
    to: Date | null,
    states: string[],
    types: string[],
    assigned: boolean | null,
    published: boolean | null,
    concluded: boolean | null,
    cache: boolean,
    orderNumber: string,
  ): Promise<Service[]> {
    const _result = await apolloClient.defaultClient.query({
      query: LIST_SERVICES_OPERATOR,
      variables: {
        start: from,
        end: to,
        states: states,
        types: types,
        assigned: assigned === null ? null : assigned,
        published: published === null ? null : published,
        concluded: concluded === null ? null : concluded,
        orderNumber: orderNumber || null,
      },
      fetchPolicy: 'no-cache',
    });
    const _services = Sort.arraySortBy(_result.data.listServices.results, (service) => [service.details.start], 1);
    if (cache) {
      Stores.operatorData.servicesCache = _services;
    }
    return _services;
  }

  public async fetchServicesPaginated({
    from,
    to,
    states,
    types,
    assigned,
    published,
    concluded,
    orderNumber,
    take,
    skip,
    orderBy,
    sortOrder,
  }: {
    from: Date | null;
    to: Date | null;
    states: string[];
    types: string[];
    assigned: boolean | null;
    published: boolean | null;
    concluded: boolean | null;
    orderNumber: string;
    take: number;
    skip: number;
    orderBy: string;
    sortOrder: 'asc' | 'desc';
  }): Promise<{ results: Service[]; totalCount: number }> {
    const _result = await apolloClient.defaultClient.query({
      query: LIST_SERVICES_PAGINATED_OPERATOR,
      variables: {
        start: from,
        end: to,
        states,
        types,
        assigned: assigned === null ? null : assigned,
        published: published === null ? null : published,
        concluded: concluded === null ? null : concluded,
        orderNumber: orderNumber || null,
        take,
        skip,
        orderBy,
        sortOrder,
      },
      fetchPolicy: 'no-cache',
    });
    const { results, totalCount } = _result.data.listServicesPaginated;
    return { results, totalCount };
  }

  /**
   * fetch a list of services to display in a listing
   *
   */
  public async fetchServiceLogisticItems({
    take,
    skip,
    orderBy,
    sortOrder,
    filters,
    cache,
  }: {
    take: number;
    skip: number;
    orderBy: string | null;
    sortOrder: string | null;
    filters: Filters;
    cache: boolean;
  }): Promise<{ results: ServiceWithLogistics[]; totalCount: number }> {
    const _result = await apolloClient.defaultClient.query({
      query: LIST_SERVICES_WITH_LOGISTICS_OPERATOR,
      variables: {
        take: take,
        skip: skip,
        orderBy: orderBy,
        sortOrder: sortOrder,
        ...filters,
      },
      ...(cache ? {} : { fetchPolicy: 'no-cache' }),
    });
    const { totalCount, results } = _result.data.listServicesWithLogisticsPaginated;
    return {
      results: results,
      totalCount: totalCount,
    };
  }

  /**
   * fetch a full single service with all available data
   *
   */
  public async fetchSingleService(service_id: string): Promise<FullService> {
    const _result = await apolloClient.defaultClient.query({
      query: SERVICE_DETAIL_OPERATOR,
      variables: {
        serviceID: service_id,
      },
      fetchPolicy: 'no-cache',
    });
    return {
      ..._result.data.listServices[0],
      secondaryServiceArea: _result.data.listServices[0].serviceArea.name,
      secondaryServiceAreaAddress: _result.data.listServices[0].serviceArea.address
        ? `${_result.data.listServices[0].serviceArea.address.street} ${_result.data.listServices[0].serviceArea.address.houseNr} ${_result.data.listServices[0].serviceArea.address.city} ${_result.data.listServices[0].serviceArea.address.zipCode} ${_result.data.listServices[0].serviceArea.address.country}`
        : '',
    };
  }

  /**
   * fetch service information (check list) for given service
   */
  public async fetchServiceInformation(service_id: string): Promise<ServiceInformation> {
    const _result = await apolloClient.defaultClient.query({
      query: SERVICE_INFORMATION,
      variables: {
        serviceID: service_id,
      },
      fetchPolicy: 'no-cache',
    });
    return _result.data.getServiceInformation;
  }

  /**
   * update service information (check list) for given service
   *
   */
  public async updateServiceInformation(service_id: string, substitute_id: string, serviceInformation: ServiceInformation): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: SUBMIT_SUBSTITUTE_CHECKLIST,
      variables: {
        serviceID: service_id,
        substituteID: substitute_id,
        defaultContract: serviceInformation.default_contract,
        contractInfo: serviceInformation.contract_info,
        bagInfo: serviceInformation.bag_info,
        cardreaderInfo: serviceInformation.cardreader_info,
        medicalDocumentsInfo: serviceInformation.medical_documents_info,
        miscInfo: serviceInformation.misc_info,
      },
    });
    return _result.data.submitServiceInformation;
  }

  /**
   * add a service for a partner
   *
   */
  public async addServiceForPartner(user_id: string, service: ServiceInput, serviceArea_id: string, service_type: string): Promise<string> {
    if (service_type !== '') {
      if (service_type === 'mobile') {
        const _result = await apolloClient.defaultClient.mutate({
          mutation: SUBMIT_MOBILESERVICE_OPERATOR,
          variables: {
            userID: user_id,
            totalCount: 1,
            publish: false,
            services: [
              {
                serviceAreaMobileID: serviceArea_id,
                details: { ...service, kvShareRateMobile: String(service.kvShareRateMobile), kvShareRateOffice: String(service.kvShareRateOffice) },
              },
            ],
          },
        });

        return _result.data.submitMobileServices[0];
      }

      if (service_type === 'office') {
        const _result = await apolloClient.defaultClient.mutate({
          mutation: SUBMIT_OFFICESERVICE_OPERATOR,
          variables: {
            userID: user_id,
            totalCount: 1,
            publish: false,
            services: [
              {
                serviceAreaOfficeID: serviceArea_id,
                details: { ...service, kvShareRateMobile: String(service.kvShareRateMobile), kvShareRateOffice: String(service.kvShareRateOffice) },
              },
            ],
          },
        });

        return _result.data.submitOfficeServices[0];
      }

      return '';
    } else {
      return '';
    }
  }

  /**
   * update an existing service
   *
   */
  public async updateService(service: FullService): Promise<boolean> {
    const _assigned: boolean = service.details.published === false && service.details.substitute !== null;
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE,
      variables: {
        serviceID: service.details.id,
        comment: service.details.comment,
        memo: service.details.memo,
        establishmentDoctorID: service.details.establishmentDoctor.id,
        regularFeeRange: {
          min: service.details.regularFeeRange.min,
          max: service.details.regularFeeRange.max,
          current: service.details.regularFeeRange.current,
        },
        start: service.details.start,
        end: service.details.end,
        break: service.details.break,
        establishmentID: service.details.establishment.id,
        kind: service.details.kind,
        partnerConfirmedContractOn: service.details.partnerConfirmedContractOn,
        substituteConfirmedContractOn: service.details.substituteConfirmedContractOn,
        substituteConfirmedServiceInfoOn: service.details.substituteConfirmedServiceInfoOn,
        stateInsuredPatientCount: _assigned === true ? Number(service.details.stateInsuredPatientCount) : null,
        privatePatientCount: _assigned === true ? Number(service.details.privatePatientCount) : null,
        necropsyCount: _assigned === true ? Number(service.details.necropsyCount) : null,
        consultationViaTelephoneCount: _assigned === true ? Number(service.details.consultationViaTelephoneCount) : null,
        instantAssign: service.details.instantAssign,
        priority: service.details.priority,
        allinclusive: service.details.allinclusive,
        kvShareRateMobile: String(service.details.kvShareRateMobile),
        kvShareRateOffice: String(service.details.kvShareRateOffice),
        discount: parseFloat(service.details.discount as unknown as string),
        parentServiceId: service.details.parentServiceId,
      },
    });

    return _result.data.updateService;
  }

  /**
   * toggle the contract on or off for the substitute of a service
   *
   */
  public async toggleContractSubstitute(service_id: string, confirmContract: boolean): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE,
      variables: {
        serviceID: service_id,
        substituteConfirmedContract: confirmContract,
      },
    });

    return _result.data.updateService;
  }

  /**
   * toggle the contract on or off for the partner
   *
   */
  public async toggleContractPartner(service_id: string, confirmContract: boolean): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE,
      variables: {
        serviceID: service_id,
        partnerConfirmedContract: confirmContract,
      },
    });

    return _result.data.updateService;
  }

  public async updateServiceLogistics(serviceLogistsics: Partial<ServiceLogistics>): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE_LOGISTICS,
      variables: {
        id: serviceLogistsics.id,
        logisticsStatus: serviceLogistsics.logisticsStatus,
        partnerRequiresFolderDelivery: serviceLogistsics.partnerRequiresFolderDelivery,
        partnerRequiresDoctorsBagDelivery: serviceLogistsics.partnerRequiresDoctorsBagDelivery,
        partnerRequiresCardReader: serviceLogistsics.partnerRequiresCardReader,
        cardReaderSerialNumbers: serviceLogistsics.cardReaderSerialNumbers,
        substituteHasDoctorsBag: serviceLogistsics.substituteHasDoctorsBag,
      },
    });

    return _result.data.updateServiceLogistics;
  }

  /**
   * Publish existing service
   *
   */
  public async publishService(service_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: PUBLISH_SERVICE,
      variables: {
        serviceID: service_id,
      },
    });
    return _result.data.publishService;
  }

  /**
   * delete an existing service
   *
   */
  public async deleteService(service_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_SERVICE,
      variables: {
        serviceID: service_id,
      },
    });
    return _result.data.deleteService;
  }

  public async duplicateService(service_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DUPLICATE_SERVICE,
      variables: {
        serviceID: service_id,
      },
    });
    return _result.data.duplicateService;
  }

  /**
   * conclude an existing service
   *
   */
  public async concludeService(service_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: CONCLUDE_SERVICE,
      variables: {
        serviceID: service_id,
      },
    });
    return _result.data.concludeService;
  }

  /**
   * assign a service to a given substitute
   *
   */
  public async assignService(service_id: string, substitute_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: ASSIGN_SERVICE,
      variables: {
        serviceID: service_id,
        substituteID: substitute_id,
      },
    });
    return _result.data.assignService;
  }

  /**
   * add an applicant to a given service
   *
   */
  public async addApplication(service_id: string, substitute_id: string, offer: number): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: ADD_APPLICANT_TO_SERVICE_OPERATOR,
      variables: {
        serviceID: service_id,
        substituteID: substitute_id,
        offer: parseFloat(offer as unknown as string),
        chainCandidate: false,
      },
    });
    return _result.data.apply === 'true' ? true : false;
  }

  /**
   * revoke application to a service
   *
   */
  public async revokeApplication(service_id: string, substitute_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: REVOKE_APPLICATION_OPERATOR,
      variables: {
        serviceID: service_id,
        substituteID: substitute_id,
      },
    });
    return _result.data.revokeApplication;
  }

  /**
   * revoke assignment of a given service
   *
   */
  public async revokeServiceAssignment(service_id: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: DELETE_SERVICE_ASSIGNEMENT,
      variables: {
        serviceID: service_id,
      },
    });
    return _result.data.revokeAssignment;
  }

  /**
   * fetch all available service submissions
   *
   */
  public async fetchServiceSubmissions(): Promise<void> {
    //TODO implement
  }

  /**
   * fetch all available service areas for a given state
   *
   */
  public async fetchServiceAreas(state: string, cache: boolean): Promise<ServiceArea[]> {
    const _result = await apolloClient.defaultClient.query({
      query: SERVICE_AREAS_OPERATOR,
      variables: {
        state: state === '' ? null : state,
      },
      fetchPolicy: 'no-cache',
    });

    // set cache in store if needed
    if (cache) {
      Stores.operatorData.serviceAreasCache = _result.data.listServiceAreas;
    }
    return _result.data.listServiceAreas;
  }

  /**
   * fetch a single service area with all available data
   *
   */
  public async fetchSingleServiceArea(serviceArea_id: string): Promise<ServiceArea> {
    const _result = await apolloClient.defaultClient.query({
      query: OPERATOR_LOAD_SERVICE_AREA,
      variables: {
        serviceAreaID: serviceArea_id,
      },
      fetchPolicy: 'no-cache',
    });

    return _result.data.listServiceAreaLocations[0];
  }

  /**
   * create a service area
   *
   */
  public async createServiceArea(new_serviceArea: ServiceArea): Promise<string> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: CREATE_SERVICE_AREA,
      variables: {
        name: new_serviceArea.name,
        state: new_serviceArea.state,
        validFrom: new_serviceArea.validFrom,
        validTo: new_serviceArea.validTo,
      },
    });

    if (Validation.validateUUIDPattern(_response.data.createServiceArea)) {
      return _response.data.createServiceArea;
    }
    return '';
  }

  /**
   * update an existing service area
   *
   */
  public async updateServiceArea(new_serviceArea: ServiceArea): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE_AREA,
      variables: {
        id: new_serviceArea.id,
        name: new_serviceArea.name,
        state: new_serviceArea.state,
        validFrom: new_serviceArea.validFrom,
        validTo: new_serviceArea.validTo,
      },
    });
    return _result.data.updateServiceArea;
  }

  /**
   * add a mobile service to a given service area
   *
   */
  public async addMobileService(serviceAreaId: string, new_mobileService: ServiceAreaMobile): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: CREATE_SERVICE_AREA_MOBILE,
      variables: {
        id: serviceAreaId,
        name: new_mobileService.name,
        shorthandName: new_mobileService.shorthandName,
        coordinates: new_mobileService.coordinates,
        comment: new_mobileService.comment,
        confidentialComment: new_mobileService.confidentialComment,
        legacyID: new_mobileService.legacyID === null ? null : Number(new_mobileService.legacyID),
      },
    });
    if (_result.data.createServiceAreaMobile === true) {
      const _index: number = Stores.operatorData.serviceAreasCache.findIndex((s: ServiceArea) => s.id === serviceAreaId);
      if (_index !== -1) {
        const _updatedServiceArea = await this.fetchSingleServiceArea(serviceAreaId);
        Stores.operatorData.serviceAreasCache[_index] = _updatedServiceArea;
      }
    }
    return _result.data.createServiceAreaMobile;
  }

  /**
   * add an office service to a given service area
   *
   */
  public async addOfficeService(serviceAreaId: string, new_officeService: ServiceAreaOffice): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: CREATE_SERVICE_AREA_OFFICE,
      variables: {
        id: serviceAreaId,
        name: new_officeService.name,
        shorthandName: new_officeService.shorthandName,
        address: {
          city: new_officeService.address.city,
          country: new_officeService.address.country,
          street: new_officeService.address.street,
          houseNr: new_officeService.address.houseNr,
          zipCode: new_officeService.address.zipCode,
          state: new_officeService.address.state,
        },
        comment: new_officeService.comment,
        confidentialComment: new_officeService.confidentialComment,
        legacyID: new_officeService.legacyID === null ? null : Number(new_officeService.legacyID),
      },
    });
    if (_result.data.createServiceAreaOffice === true) {
      const _index: number = Stores.operatorData.serviceAreasCache.findIndex((s: ServiceArea) => s.id === serviceAreaId);
      if (_index !== -1) {
        const _updatedServiceArea = await this.fetchSingleServiceArea(serviceAreaId);
        Stores.operatorData.serviceAreasCache[_index] = _updatedServiceArea;
      }
    }
    return _result.data.createServiceAreaOffice;
  }

  /**
   * Update an existing office service - office services have addresses, so we need
   * to chain two mutations to update the address if the update of the office service
   * itself worked (returned true)
   *
   *
   */
  public async updateOfficeService(new_officeService: ServiceAreaOffice): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE_AREA_OFFICE,
      variables: {
        id: new_officeService.id,
        name: new_officeService.name,
        shorthandName: new_officeService.shorthandName,
        address: new_officeService.address,
        comment: new_officeService.comment,
        confidentialComment: new_officeService.confidentialComment,
        legacyID: new_officeService.legacyID === null ? null : Number(new_officeService.legacyID),
      },
    });
    if (_result.data.updateServiceAreaOffice === true) {
      const _addressResult = await this.updateAddress(new_officeService.address);
      return _addressResult;
    } else {
      return false;
    }
  }

  /**
   * update an existing mobile service
   *
   */
  public async updateMobileService(new_mobileService: ServiceAreaMobile): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SERVICE_AREA_MOBILE,
      variables: {
        id: new_mobileService.id,
        name: new_mobileService.name,
        shorthandName: new_mobileService.shorthandName,
        coordinates: new_mobileService.coordinates,
        comment: new_mobileService.comment,
        confidentialComment: new_mobileService.confidentialComment,
        legacyID: new_mobileService.legacyID === null ? null : Number(new_mobileService.legacyID),
      },
    });
    return _result.data.updateServiceAreaMobile;
  }

  /**
   * update an existing address
   *
   */
  public async updateAddress(new_address: Address): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_ADDRESSES,
      variables: {
        addresses: [
          {
            id: new_address.id,
            city: new_address.city,
            state: new_address.state,
            country: new_address.country,
            street: new_address.street,
            houseNr: new_address.houseNr,
            zipCode: new_address.zipCode,
            addressSupplement: new_address.addressSupplement,
          },
        ],
      },
    });
    return _result.data.updateAddresses[0];
  }

  /**
   * update an existing bank account
   *
   */
  public async updateBankAccount(bankAccount: BankAccount): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_BANKACCOUNTS_OPERATOR,
      variables: {
        accounts: [
          {
            id: bankAccount.id,
            accountHolder: bankAccount.accountHolder,
            bank: bankAccount.bank,
            iban: bankAccount.iban,
            bic: bankAccount.bic,
          },
        ],
      },
    });
    return _result.data.updateBankAccounts;
  }

  public async updateLegacyInformation(user_id: string, bonus_fee: string, bag_handling_fee: string): Promise<boolean> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_LEGACY_INFORMATION,
      variables: {
        userID: user_id,
        bonusFee: parseInt(bonus_fee),
        bagHandlingFee: parseInt(bag_handling_fee),
      },
    });
    return _result.data.updateLegacyInformation;
  }

  /**
   * switch a debtor to bill payments
   *
   */
  public async switchToBillPaymentMethod(
    debtor_id: string,
    bankAccount: BankAccountInput,
    debtorSepaPaymentMethodID: string,
    withSetTariff: boolean = true,
  ): Promise<boolean> {
    // delete former payment method
    const _deleteResult = await apolloClient.defaultClient.mutate({
      mutation: DELETE_DEBTOR_SEPA_PAYMENT_METHOD,
      variables: {
        debtorSepaPaymentMethodID: debtorSepaPaymentMethodID,
      },
    });

    if (_deleteResult.data.deleteDebtorSepaPaymentMethod === true) {
      // add new payment method
      const _result = await apolloClient.defaultClient.mutate({
        mutation: ADD_PAYMENT_METHOD_BILL,
        variables: {
          debtorID: debtor_id,
          bankAccount: bankAccount,
        },
      });

      if (Validation.validateUUIDPattern(_result.data.addDebtorBillPaymentMethod) && withSetTariff) {
        const _tariffResult = await apolloClient.defaultClient.mutate({
          mutation: SET_PARTNER_TARIFF,
          variables: {
            debtorID: debtor_id,
            period: 12,
            paymentPeriod: 12,
            paymentMethodID: _result.data.addDebtorBillPaymentMethod,
          },
        });
        return _tariffResult.data.setPartnerDebtorTariff;
      }
      return false;
    }

    return false;
  }

  /**
   * Add a tariff and bill payment to debtor
   * @param {Debtor} debtor - The debtor to repair tariff for
   * @returns {Promise<boolean>} - Success indicator
   */
  public async repairTariff(debtor: Debtor): Promise<boolean> {
    let paymentMethodId: string;

    if (debtor.payment_method?.length > 0) {
      paymentMethodId = debtor.payment_method[0].id;
    } else {
      const [success, newId] = await this.addBillPaymentMethod(debtor.id);
      if (!success) {
        return false;
      }
      paymentMethodId = newId as string;
    }

    const result = await apolloClient.defaultClient.mutate({
      mutation: SET_PARTNER_TARIFF,
      fetchPolicy: 'no-cache',
      variables: {
        debtorID: debtor.id,
        period: 12,
        paymentPeriod: 12,
        paymentMethodID: paymentMethodId,
      },
    });

    return result.data.setPartnerDebtorTariff;
  }

  public async deleteDebtorBillPaymentMethod(debtorBillPaymentMethodID: string): Promise<boolean> {
    const _deleteResult = await apolloClient.defaultClient.mutate({
      mutation: DELETE_DEBTOR_BILL_PAYMENT_METHOD,
      variables: {
        debtorBillPaymentMethodID: debtorBillPaymentMethodID,
      },
    });

    return _deleteResult.data.deleteDebtorBillPaymentMethod;
  }

  public async deleteDebtorSepaPaymentMethod(debtorSepaPaymentMethodID: string): Promise<boolean> {
    const _deleteResult = await apolloClient.defaultClient.mutate({
      mutation: DELETE_DEBTOR_SEPA_PAYMENT_METHOD,
      variables: {
        debtorSepaPaymentMethodID: debtorSepaPaymentMethodID,
      },
    });

    return _deleteResult.data.deleteDebtorSepaPaymentMethod;
  }

  public async addBillPaymentMethod(debtor_id: string, bankAccount?: BankAccountInput): Promise<[boolean, string | null]> {
    // add new payment method
    const _result = await apolloClient.defaultClient.mutate({
      mutation: ADD_PAYMENT_METHOD_BILL,
      variables: {
        debtorID: debtor_id,
        bankAccount,
      },
    });

    const paymentMethodId = _result.data.addDebtorBillPaymentMethod;
    const isValid = Validation.validateUUIDPattern(paymentMethodId);

    // Return tuple with validation result and the original value (if valid)
    return [isValid, isValid ? paymentMethodId : null];
  }

  public async addSepaPaymentMethod(debtor_id: string, bankAccount: BankAccountInput, expirationDate: Date): Promise<boolean> {
    // add new payment method
    const _result = await apolloClient.defaultClient.mutate({
      mutation: ADD_PAYMENT_METHOD_SEPA,
      variables: {
        debtorID: debtor_id,
        bankAccount: bankAccount,
        expirationDate: DateTime.dateToIsoDateString(expirationDate),
      },
    });

    return Validation.validateUUIDPattern(_result.data.addDebtorSepaPaymentMethod);
  }

  /**
   * switch a debtor to sepa payments
   *
   */
  public async switchToSepaPaymentMethod(
    debtor_id: string,
    bankAccount: BankAccountInput,
    expirationDate: Date,
    debtorBillPaymentMethodID: string,
    withSetTariff: boolean = true,
  ): Promise<boolean> {
    // delete former payment method
    const _deleteResult = await apolloClient.defaultClient.mutate({
      mutation: DELETE_DEBTOR_BILL_PAYMENT_METHOD,
      variables: {
        debtorBillPaymentMethodID: debtorBillPaymentMethodID,
      },
    });

    if (_deleteResult.data.deleteDebtorBillPaymentMethod === true) {
      // add new payment method
      const _result = await apolloClient.defaultClient.mutate({
        mutation: ADD_PAYMENT_METHOD_SEPA,
        variables: {
          debtorID: debtor_id,
          bankAccount: bankAccount,
          expirationDate: DateTime.dateToIsoDateString(expirationDate),
        },
      });

      if (Validation.validateUUIDPattern(_result.data.addDebtorSepaPaymentMethod) && withSetTariff) {
        const _tariffResult = await apolloClient.defaultClient.mutate({
          mutation: SET_PARTNER_TARIFF,
          variables: {
            debtorID: debtor_id,
            period: 12,
            paymentPeriod: 12,
            paymentMethodID: _result.data.addDebtorSepaPaymentMethod,
          },
        });
        return _tariffResult.data.setPartnerDebtorTariff;
      }
      return false;
    }

    return false;
  }

  public async getSubstituteBlacklist(substitute_id: string): Promise<string[]> {
    const _result = await apolloClient.defaultClient.query({
      query: GET_SUBSITUTE_BLACKLIST,
      variables: {
        substituteID: substitute_id,
      },
      fetchPolicy: 'no-cache',
    });
    return _result.data.getOperatorBlacklistedStates;
  }

  public async getFileFromS3(filePath: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: GET_FILE_FROM_S3,
      variables: {
        filePath: filePath,
      },
      fetchPolicy: 'no-cache',
    });
    return _result.data.getFileFromS3;
  }

  public async provideCognitoAccess(token: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: PROVIDE_COGNITO_ACCESS,
      variables: {
        token: token,
      },
      fetchPolicy: 'no-cache',
    });
    return _result.data.provideCognitoAccess;
  }

  /**
   * update blacklist information for a substitute
   * @return {Promise.<boolean>}
   */
  public async updateSubstituteBlacklist(substitute_id: string, states: string[]): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_BLACKLIST,
      variables: {
        substituteID: substitute_id,
        states: states,
      },
    });
    if (_response.data.updateSubstituteServiceNewsletterBlacklist == true) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * fetch Event Log for object
   *
   */
  public async fetchEvents(object_type: string, object_id: string): Promise<Event[]> {
    let hasNextPage: boolean = true;
    let afterCursor: string | null = null;
    const allEvents: Event[] = [];

    while (hasNextPage) {
      const _result: ApolloQueryResult<ListEventsResult> = await apolloClient.defaultClient.query({
        query: LIST_EVENTS,
        variables: {
          objectType: object_type,
          objectId: object_id,
          first: 5,
          after: afterCursor,
        },
        fetchPolicy: 'no-cache',
      });
      _result.data.listEvents.edges.forEach((edge: { node: Event }) => {
        allEvents.push(edge.node);
      });
      hasNextPage = _result.data.listEvents.pageInfo.hasNextPage;
      afterCursor = _result.data.listEvents.pageInfo.endCursor;
    }
    return allEvents;
  }

  /**
   * get a current download link for a substitute license file
   * @return {Promise.<string>} - the url for the download
   */
  public async getSubstituteLicenseDownloadLink(user_id: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: FETCH_SUBSTITUTE_LICENSE_OPERATOR,
      variables: {
        userID: user_id,
      },
      fetchPolicy: 'no-cache',
    });
    if (_result.data.listCustomers[0].substitute.license !== null) {
      return _result.data.listCustomers[0].substitute.license.url;
    } else {
      return '';
    }
  }

  /**
   * get a current download link for substitute specialization certificate file
   * @return {Promise.<string>} - the url for the download
   */
  public async getSubstituteCertificateDownloadLink(user_id: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: FETCH_SUBSTITUTE_SPECIALIZATIONS_OPERATOR,
      variables: {
        userID: user_id,
      },
      fetchPolicy: 'no-cache',
    });
    if (_result.data.listCustomers[0].substitute.specializations.length > 0) {
      return _result.data.listCustomers[0].substitute.specializations[0].certificate.url;
    } else {
      return '';
    }
  }

  public async getCustomerLogisticsPreferences(debtorNumber: number): Promise<{
    id: string;
    partnerLogisticsPreferences: {
      id: string;
      requiresFolderDelivery: boolean;
      requiresDoctorsBagDelivery: boolean;
      requiresCardReader: boolean;
      cardReaderSerialNumbers: string[];
    };
  } | null> {
    const _result = await apolloClient.defaultClient.query({
      query: FETCH_CUSTOMER_LOGISTICS_OPERATOR,
      variables: {
        debtorNumber: debtorNumber,
      },
      fetchPolicy: 'no-cache',
    });
    if (_result.data.listCustomers[0].id) {
      return _result.data.listCustomers[0];
    } else {
      return null;
    }
  }

  /**
   * get a current download link for a substitute insurance file
   * @return {Promise.<string>} - the url for the download
   */
  public async getSubstituteInsuranceDownloadLink(user_id: string, insurance_id: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: FETCH_SUBSTITUTE_INSURANCES_OPERATOR,
      variables: {
        userID: user_id,
      },
      fetchPolicy: 'no-cache',
    });
    for (const _insurance of _result.data.listCustomers[0].substitute.liabilityInsurances) {
      if (_insurance.id === insurance_id) {
        return _insurance.document.url;
      }
    }
    return '';
  }

  public async getSubstituteMeaslesProofDownloadLink(user_id: string, measles_proof_id: string): Promise<string> {
    const _result = await apolloClient.defaultClient.query({
      query: FETCH_SUBSTITUTE_MEASLES_PROOF_OPERATOR,
      variables: {
        userID: user_id,
      },
      fetchPolicy: 'no-cache',
    });
    console.log(_result);
    for (const _proof of _result.data.listCustomers[0].substitute.measlesProofs) {
      if (_proof.id === measles_proof_id) {
        return _proof.document.url;
      }
    }
    return '';
  }

  /**
   * Create a new substitute user
   */
  public async createSubstitute(
    email: string,
    title: string,
    gender: string | null,
    firstName: string,
    lastName: string,
    birthDate: Date,
    mobile1: string | null,
    mobile2: string | null,
    phone: string | null,
    fax: string | null,
    trainingConfirmed: boolean,
    taxNumber: string,
    address: AddressInput,
    bankAccount: BankAccountInput,
    specialization: SubstituteSpecializationInput,
  ): Promise<string> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: CREATE_SUBSTITUTE_OPERATOR,
      variables: {
        email,
        title,
        gender,
        firstName,
        lastName,
        birthDate: DateTime.dateToIsoDateString(birthDate),
        mobile1,
        mobile2,
        phone,
        fax,
        trainingConfirmed,
        taxNumber,
        address,
        bankAccount,
        specialization,
      },
    });

    if (Validation.validateUUIDPattern(_result.data.createSubstituteOperator)) {
      return _result.data.createSubstituteOperator;
    }
    return '';
  }

  /**
   * Create a new partner user
   */
  public async createPartner(
    email: string,
    title: string,
    gender: string | null,
    firstName: string,
    lastName: string,
    birthDate: Date,
    mobile1: string | null,
    mobile2: string | null,
    phone: string | null,
    fax: string | null,
    institution: InstitutionInput,
    establishment: EstablishmentInput,
    establishmentDoctor?: EstablishmentDoctorInput,
  ): Promise<string> {
    const _result = await apolloClient.defaultClient.mutate({
      mutation: CREATE_PARTNER_OPERATOR,
      variables: {
        email,
        title,
        gender,
        firstName,
        lastName,
        birthDate: DateTime.dateToIsoDateString(birthDate),
        mobile1,
        mobile2,
        phone,
        fax,
        institution,
        establishment,
        establishmentDoctor,
      },
    });

    if (Validation.validateUUIDPattern(_result.data.createPartnerOperator)) {
      return _result.data.createPartnerOperator;
    }
    return '';
  }

  /**
   * fetch services for accounting (assigned services)
   * @param {Date | null} from
   * @param {Date | null} to
   * @param {boolean} creationDate - switch between creationDate and service start date as a search paran (true: creationDate | false: service start date)
   */
  public async fetchServicesForAccounting(from: Date | null, to: Date | null, creationDate: boolean): Promise<FullService[]> {
    let _services = [];

    let _where: Object = {};

    if (from === null && to === null) {
      _where = {
        service: {
          state: {
            equals: 'assigned',
          },
        },
      };
    }

    if (from) {
      if (creationDate) {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                created_on: {
                  gte: from,
                },
              },
            ],
          },
        };
      } else {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                start: {
                  gte: from,
                },
              },
            ],
          },
        };
      }
    }

    if (to) {
      if (creationDate) {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                created_on: {
                  lte: to,
                },
              },
            ],
          },
        };
      } else {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                start: {
                  lte: to,
                },
              },
            ],
          },
        };
      }
    }

    if (from && to) {
      if (creationDate) {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                created_on: {
                  gte: from,
                },
              },
              {
                created_on: {
                  lte: to,
                },
              },
            ],
          },
        };
      } else {
        _where = {
          service: {
            AND: [
              {
                state: {
                  equals: 'assigned',
                },
              },
              {
                start: {
                  gte: from,
                },
              },
              {
                start: {
                  lte: to,
                },
              },
            ],
          },
        };
      }
    }

    const _mobileServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_MOBILE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _mobileServices.data.mobileServices) {
      if (_service.details.substitute !== null) {
        _services.push(_service);
      }
    }

    const _officeServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_OFFICE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _officeServices.data.officeServices) {
      if (_service.details.substitute !== null) {
        _services.push(_service);
      }
    }

    _services = Sort.arraySortBy(_services, (service: FullService) => [service.details.createdOn], 1);
    return _services;
  }

  /**
   * fetch services for reporting
   *
   */
  public async fetchServicesForReporting(from: Date | null, to: Date | null): Promise<FullService[]> {
    let _services = [];

    let _where: Object = {};

    if (from) {
      _where = {
        service: {
          created_on: {
            gte: from,
          },
        },
      };
    }

    if (to) {
      _where = {
        service: {
          created_on: {
            lte: to,
          },
        },
      };
    }

    if (from && to) {
      _where = {
        service: {
          AND: [
            {
              created_on: {
                gte: from,
              },
            },
            {
              created_on: {
                lte: to,
              },
            },
          ],
        },
      };
    }

    const _mobileServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_MOBILE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _mobileServices.data.mobileServices) {
      _services.push(_service);
    }

    const _officeServices = await apolloClient.defaultClient.query({
      query: OPERATOR_FETCH_ACCOUNTING_OFFICE,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    // this is a workaround as the backend doesn't update the state from "assigned" back to "published" when
    // the substitute gets unassigned
    // TODO: update backend
    for (const _service of _officeServices.data.officeServices) {
      _services.push(_service);
    }

    _services = Sort.arraySortBy(_services, (service: FullService) => [service.details.createdOn], 1);
    return _services;
  }

  public async fetchUsersForReporting(from: Date | null, to: Date | null): Promise<User[]> {
    let _users = [];

    let _where: Object = {};

    if (from) {
      _where = {
        registration_date: {
          gte: from,
        },
      };
    }

    if (to) {
      _where = {
        registration_date: {
          lte: to,
        },
      };
    }

    if (from && to) {
      _where = {
        AND: [
          {
            registration_date: {
              gte: from,
            },
          },
          {
            registration_date: {
              lte: to,
            },
          },
        ],
      };
    }

    const _result = await apolloClient.defaultClient.query({
      query: LIST_USERS_FUZZY_OPERATOR,
      variables: {
        where: _where,
      },
      fetchPolicy: 'no-cache',
    });

    _users = Sort.arraySortBy(_result.data.users, (user: User) => [user.registrationDate], 1);
    return _users;
  }

  /**
   * send service advertisement mail to substitutes
   * @param subject
   * @param {string} content
   * @param {string} state
   * @return {Promise.<boolean>}
   */
  public async sendServiceAdvertisementByState(subject: string, content: string, state: string): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_SERVICE_NEWSLETTER_BY_STATE_OPERATOR,
      variables: {
        subject: subject,
        content: content,
        state: state,
      },
    });
    return _response.data.sendServiceNewsletterEmail;
  }

  /**
   * send service advertisement mail to substitutes by state
   * @param subject
   * @param {string} content
   * @param {string} state
   * @return {Promise.<boolean>}
   */
  public async sendServiceAdvertisement(subject: string, content: string, substitute_id: string): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_SERVICE_NEWSLETTER_OPERATOR,
      variables: {
        subject: subject,
        content: content,
        state: '',
        substituteID: substitute_id === '' ? null : substitute_id,
      },
    });
    return _response.data.sendServiceNewsletterEmailByState;
  }

  /**
   * send mass advertisement mail to target users
   * @param {string} subject
   * @param {string} content
   * @param {string} targetGroup
   * @param {string[]} roles
   * @param {string} state
   * @param {string} userID
   * @return {Promise.<boolean>}
   */
  public async sendUsersMassEmail(
    subject: string,
    content: string,
    targetGroup: string,
    roles: string[],
    state: string | undefined,
    userID: string | undefined,
    attachments: File[],
  ): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_USERS_MASS_EMAIL_OPERATOR,
      variables: {
        subject: subject,
        content: content,
        targetGroup: targetGroup,
        roles: roles,
        state: state,
        userID: userID,
        attachments: await Promise.all(attachments.map(async (it) => await Upload.getUploadDataWithName(it, it.name))),
      },
    });
    return _response.data.sendUsersMassEmails;
  }

  /**
   * deactivate a user
   * @return {Promise}
   */
  public async deactivateUser(userID: String, reason: String): Promise<void> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: DEACTIVATE_USER,
      variables: {
        userID,
        reason,
      },
    });
    return _response.data;
  }
  /**
   * reactivate a user
   * @return {Promise}
   */
  public async reactivateUser(userID: String): Promise<void> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: REACTIVATE_USER,
      variables: {
        userID,
      },
    });
    return _response.data;
  }
  /**
   * * send confirmation order
   * @return {Promise.<boolean>}
   */
  public async sendOrderConfirmation(customerId: string, email: string, fax: string, assignments: Item[], currentDateTime: string): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_ORDER_CONFIMRATION_OPERATOR,
      variables: {
        customerId,
        email,
        fax,
        assignments,
        currentDateTime,
      },
    });
    return _response.data.sendOrderConfirmation;
  }
  public async produceKafkaServices(): Promise<string> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: KAFKA_PRODUCE_ALL_SERVICES,
    });
    return _response.data.kafkaProduceAllServices;
  }
  /**
   * * send summary order
   * @return {Promise.<boolean>}
   */
  public async sendOrderSummary(
    substituteId: string,
    email: string,
    name: string,
    id: string,
    from: string,
    to: string,
    assignments: Item[],
  ): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_ORDER_SUMMARY_OPERATOR,
      variables: {
        substituteId,
        email,
        name,
        id,
        from,
        to,
        assignments,
      },
    });
    return _response.data.sendOrderSummary;
  }

  /**
   * * send documents
   * @return {Promise.<boolean>}
   */
  public async sendDocuments(email: string, serviceId: string, notice: File | null, contract: File | null): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SEND_DOCUMENTS_OPERATOR,
      variables: {
        email,
        serviceId,
        notice: notice ? await Upload.getUploadDataWithName(notice, 'Tauschmeldung.pdf') : null,
        contract: contract ? await Upload.getUploadDataWithName(contract, 'Dienstvertretungsvertrag.pdf') : null,
      },
    });
    return _response.data.sendDocuments;
  }

  public async cancelService(serviceId: string, cancellationReason: string): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: CANCEL_SERVICE,
      variables: {
        serviceID: serviceId,
        cancellationReason,
      },
    });

    return _response.data.cancelService;
  }
  public async verifyImmunityProof(substituteID: string, immunityProofVerified: boolean): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: UPDATE_SUBSTITUTE_OPERATOR,
      variables: {
        substituteID,
        immunityProofVerified,
      },
    });
    return _response.data.updateSubstitute;
  }
}

export default new Operator();
